import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { updateProgress, useListeningUnitData, useVocabUnitData } from "../../../../database";
import { GAME_OPTIONS_PATH, PRACTICE_PATH, API_BASE_URL } from "../../../../constants";
import "./HideAndSeekPage.css";
import SetUpBox from "./SetUpBox";
import AudioControls from "../../GameComponents/AudioControls";
import guardImage from "./resources/guard.png";
import bgm from "./resources/hide_and_seek_bgm.mp3";
import { FIREBASE_AUTH } from "../../../../FirebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

// gameplay tuning values for board layout and flashlight feel.
const BOARD_PADDING_PERCENT = 10;
const MIN_DISTANCE_PERCENT = 9;
const FLASHLIGHT_RADIUS = 130;
const DISTRACTOR_MULTIPLIER = 4;
const MIN_DISTRACTORS = 20;
const MAX_LIVES = 3;
const GUARD_HOVER_PENALTY_DELAY_MS = 500;
const HIDE_AND_SEEK_GAME = "hideandseek";
const TIME_BY_DIFFICULTY = {
  easy: 60,
  medium: 45,
  hard: 30,
};
const GUARD_MOVE_INTERVAL_MS = {
  easy: 10000,
  medium: 7000,
  hard: 5000,
};

// backup Hangul characters if the unit data cannot provide distractors.
const FALLBACK_DISTRACTORS = [
  "가",
  "나",
  "다",
  "라",
  "마",
  "바",
  "사",
  "아",
  "자",
  "차",
  "카",
  "타",
  "파",
  "하",
];

// finds a random position that avoids stacking characters too closely.
function generatePosition(existingCharacters) {
  let attempts = 0;

  while (attempts < 80) {
    const x =
      BOARD_PADDING_PERCENT +
      Math.random() * (100 - BOARD_PADDING_PERCENT * 2);
    const y =
      BOARD_PADDING_PERCENT +
      Math.random() * (100 - BOARD_PADDING_PERCENT * 2);

    const collision = existingCharacters.some((character) => {
      const dx = character.x - x;
      const dy = character.y - y;
      return Math.sqrt(dx * dx + dy * dy) < MIN_DISTANCE_PERCENT;
    });

    if (!collision) {
      return { x, y };
    }

    attempts += 1;
  }

  return {
    x: BOARD_PADDING_PERCENT + Math.random() * (100 - BOARD_PADDING_PERCENT * 2),
    y: BOARD_PADDING_PERCENT + Math.random() * (100 - BOARD_PADDING_PERCENT * 2),
  };
}

// one round: correct answer characters + random distractors.
function createScatteredCharacters(targetWord, characterPool) {
  const scatteredCharacters = [];
  const answerCharacters = targetWord.korean.replace(/\s/g, "").split("");
  const answerCharacterSet = new Set(answerCharacters);

  answerCharacters.forEach((char, index) => {
    const position = generatePosition(scatteredCharacters);
    scatteredCharacters.push({
      id: `correct-${index}-${Math.random().toString(36).slice(2, 8)}`,
      char,
      x: position.x,
      y: position.y,
      isCorrect: true,
      isFound: false,
    });
  });

  let distractorPool = characterPool.filter(
    (char) => char && !answerCharacterSet.has(char)
  );
  if (distractorPool.length === 0) {
    distractorPool = FALLBACK_DISTRACTORS.filter(
      (char) => !answerCharacterSet.has(char)
    );
  }

  const distractorCount = Math.max(
    MIN_DISTRACTORS,
    answerCharacters.length * DISTRACTOR_MULTIPLIER
  );

  for (let i = 0; i < distractorCount; i += 1) {
    const randomChar =
      distractorPool[Math.floor(Math.random() * distractorPool.length)];
    const position = generatePosition(scatteredCharacters);

    scatteredCharacters.push({
      id: `distractor-${i}-${Math.random().toString(36).slice(2, 8)}`,
      char: randomChar || "?",
      x: position.x,
      y: position.y,
      isCorrect: false,
      isFound: false,
    });
  }

  return scatteredCharacters;
}

function HideAndSeekPage() {
  // unit comes from the route state (selected on previous screen).
  const location = useLocation();
  const unit = location.state.unit;
  const category = location.state.category;

  // listening data hook — same source as Sugar Honeycombs and Gong Gi.
  const { data: listeningData, loading, error } = useListeningUnitData(unit?.unit, unit?.textbook);
  const { data: vocabData, loading: vLoading, error: vError } = useVocabUnitData(unit?.unit, unit?.textbook);

  // Convert listeningData object to sorted array or generate from vocabData
  const listeningDataArr = useMemo(() => {
    var arr = [];
    if (unit?.textbook === "GT_textbook") {
      arr = Object.keys(listeningData)
        .map((key) => ({
          ...listeningData[key],
          question_number: parseInt(key)
        }))
        .sort((a, b) => a.question_number - b.question_number);
    } else {
      // Randomly choose words from vocabData
      function getRandomSample(arr, n) {
        const result = [];
        const used = new Set();
        while (result.length < n && used.size < arr.length) {
          const idx = Math.floor(Math.random() * arr.length);
          if (!used.has(idx)) {
            used.add(idx);
            result.push(arr[idx]);
          }
        }
        return result;
      }
      const vocabSample = vocabData && vocabData.length >= 4 ? getRandomSample(vocabData, 4) : (vocabData || []).slice(0, 4);
      arr = vocabSample.map((word) => ({
        options: [{ answer: word.korean, is_correct: 1 }],
        audio_path: word.audioPath
      }));
    }
    return arr;
  }, [unit?.textbook, JSON.stringify(listeningData), JSON.stringify(vocabData)]);

  // use the correct answer from each listening question as the word to find.
  const gameWords = useMemo(
    () =>
      listeningDataArr
        .map((entry) => {
          const correctOption = entry.options.find(opt => opt.is_correct === 1);
          return correctOption ? {
            korean: correctOption.answer.trim(),
            audioPath: entry.audio_path
          } : null;
        })
        .filter(Boolean),
    [listeningDataArr]
  );

  // collect a pool of Hangul chars from the current unit for distractors.
  const characterPool = useMemo(
    () =>
      gameWords
        .map((word) => word.korean.replace(/\s/g, ""))
        .join("")
        .split("")
        .filter(Boolean),
    [gameWords]
  );

  // board DOM ref is used for cursor math and visibility checks.
  const boardRef = useRef(null);
  const guardRef = useRef(null);
  const guardHoverTimerRef = useRef(null);
  const guardCaughtTimerRef = useRef(null);
  const guardPenaltyArmedRef = useRef(false);
  const loggedSuccessWordRef = useRef(null);
  const loggedTimeoutWordRef = useRef(null);
  const loggedLossWordRef = useRef(null);
  // core game state for current round.
  const [difficulty, setDifficulty] = useState("easy");
  const [wordIndex, setWordIndex] = useState(0);
  const [scatteredChars, setScatteredChars] = useState([]);
  const [cursorPos, setCursorPos] = useState({ x: -9999, y: -9999 });
  const [wrongId, setWrongId] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [lives, setLives] = useState(MAX_LIVES);
  const roundTimeSeconds = TIME_BY_DIFFICULTY[difficulty] || 30;
  const [timeLeft, setTimeLeft] = useState(roundTimeSeconds);
  const [guardPosition, setGuardPosition] = useState(null);
  const [showGuardImage, setShowGuardImage] = useState(true);
  const [showGuardCaughtIndicator, setShowGuardCaughtIndicator] = useState(false);
  const [user, setUser] = useState(null);

  const audioRefBgm = useRef(null);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [isLocalAudioPlaying, setIsLocalAudioPlaying] = useState(false);

  useEffect(() => {
    if (audioRefBgm.current) {
        audioRefBgm.current.volume = isMuted ? 0 : (isLocalAudioPlaying ? volume * 0.2 : volume);
    }
  }, [volume, isMuted, isLocalAudioPlaying]);

  useEffect(() => {
     if (gameStarted && audioRefBgm.current) {
        audioRefBgm.current.play().catch(console.error);
     } else if (!gameStarted && audioRefBgm.current) {
        audioRefBgm.current.pause();
        audioRefBgm.current.currentTime = 0;
     }
  }, [gameStarted]);

  // derived values for the active target word.
  const currentWord = gameWords[wordIndex];
  const correctChars = currentWord ? currentWord.korean.split("") : [];

  // round complete check: every correct character has been found.
  const allCorrectFound =
    scatteredChars.length > 0 &&
    scatteredChars.filter((character) => character.isCorrect).every(
      (character) => character.isFound
    );
  const isOutOfLives = lives <= 0;
  const isTimeOut = timeLeft <= 0;
  const isGameOver = isOutOfLives || isTimeOut;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(FIREBASE_AUTH, (nextUser) => {
      setUser(nextUser);
    });

    return unsubscribe;
  }, []);

  const saveHideAndSeekProgress = useCallback(
    (isCorrect) => {
      if (!user || !unit?.unit) {
        return;
      }

      updateProgress(
        user.email,
        unit.unit,
        unit.textbook,
        HIDE_AND_SEEK_GAME,
        isCorrect
      );
    },
    [unit?.textbook, unit?.unit, user]
  );

  const currentAudioRef = useRef(null);

  const playCurrentWordAudio = useCallback(() => {
    if (!currentWord?.audioPath) return;
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
    }
    const audioPath = `${API_BASE_URL}${currentWord.audioPath}`;
    const audio = new Audio(audioPath);
    currentAudioRef.current = audio;
    
    audio.onplay = () => setIsLocalAudioPlaying(true);
    audio.onended = () => setIsLocalAudioPlaying(false);
    audio.onpause = () => setIsLocalAudioPlaying(false);
    
    audio.play().catch((err) => console.error("Audio play error:", err));
  }, [currentWord]);

  // creates/refreshes the board for the selected word index.
  const buildRound = useCallback(
    (nextWordIndex) => {
      if (!gameWords[nextWordIndex]) return;
      const nextScattered = createScatteredCharacters(
        gameWords[nextWordIndex],
        characterPool
      );
      setScatteredChars(nextScattered);
      setGuardPosition(generatePosition(nextScattered));
      setShowGuardCaughtIndicator(false);
      loggedSuccessWordRef.current = null;
      loggedTimeoutWordRef.current = null;
      setWrongId(null);
      setTimeLeft(roundTimeSeconds);
    },
    [characterPool, gameWords, roundTimeSeconds]
  );

  // regenerates the board when unit data or active word changes.
  useEffect(() => {
    if (!gameStarted) return;
    if (gameWords.length === 0) return;

    const safeIndex = Math.min(wordIndex, gameWords.length - 1);
    if (safeIndex !== wordIndex) {
      setWordIndex(safeIndex);
      return;
    }

    buildRound(safeIndex);
  }, [buildRound, gameStarted, gameWords, wordIndex]);

  // centers the flashlight when a new board is rendered.
  useEffect(() => {
    if (!gameStarted) return;
    if (!boardRef.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    setCursorPos({ x: rect.width / 2, y: rect.height / 2 });
  }, [gameStarted, scatteredChars.length]);

  // auto-play the word whenever a new round starts.
  useEffect(() => {
    if (!gameStarted || !currentWord || isGameOver) return;
    playCurrentWordAudio();
  }, [currentWord, gameStarted, isGameOver, playCurrentWordAudio]);

  // stop audio when the component unmounts.
  useEffect(() => () => {
    if (currentAudioRef.current) currentAudioRef.current.pause();
  }, []);

  // Converts browser coordinates into board-local coordinates.
  const updateCursor = (clientX, clientY) => {
    if (!boardRef.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    setCursorPos({
      x: Math.max(0, Math.min(rect.width, clientX - rect.left)),
      y: Math.max(0, Math.min(rect.height, clientY - rect.top)),
    });
  };

  // Pointer tracking for desktop.
  const handleMouseMove = (event) => {
    updateCursor(event.clientX, event.clientY);
  };

  // Pointer tracking for touch devices.
  const handleTouchMove = (event) => {
    if (!event.touches[0]) return;
    updateCursor(event.touches[0].clientX, event.touches[0].clientY);
  };

  // Only allow clicks on characters visible inside flashlight radius.
  const isCharacterInFlashlight = (character) => {
    if (!boardRef.current) return true;
    const rect = boardRef.current.getBoundingClientRect();
    const characterX = (character.x / 100) * rect.width;
    const characterY = (character.y / 100) * rect.height;
    const dx = characterX - cursorPos.x;
    const dy = characterY - cursorPos.y;
    return Math.sqrt(dx * dx + dy * dy) <= FLASHLIGHT_RADIUS + 18;
  };

  // mark correct chars as found, briefly flash wrong clicks.
  const handleCharacterClick = (character, event) => {
    event.stopPropagation();
    if (isGameOver) return;
    if (character.isFound || !isCharacterInFlashlight(character)) return;

    if (character.isCorrect) {
      setScatteredChars((previousChars) =>
        previousChars.map((existingChar) =>
          existingChar.id === character.id
            ? { ...existingChar, isFound: true }
            : existingChar
        )
      );
      return;
    }

    setWrongId(character.id);
    setTimeout(() => setWrongId(null), 300);
    setLives((previousLives) => Math.max(0, previousLives - 1));
  };

  // countdown timer for each word; game ends when it reaches zero.
  useEffect(() => {
    if (!gameStarted) return;
    if (isGameOver) return;
    if (allCorrectFound) return;

    const timer = setInterval(() => {
      setTimeLeft((previousTime) => Math.max(0, previousTime - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [allCorrectFound, gameStarted, isGameOver]);

  // auto-advance to the next word shortly after finishing the current one.
  useEffect(() => {
    if (!gameStarted) return;
    if (isGameOver) return;
    if (!allCorrectFound || gameWords.length === 0) return;

    const autoNextTimer = setTimeout(() => {
      setWordIndex((previousIndex) => (previousIndex + 1) % gameWords.length);
    }, 700);

    return () => clearTimeout(autoNextTimer);
  }, [allCorrectFound, gameStarted, gameWords.length, isGameOver]);

  useEffect(() => {
    if (!gameStarted || !allCorrectFound) {
      return;
    }

    if (loggedSuccessWordRef.current === wordIndex) {
      return;
    }

    loggedSuccessWordRef.current = wordIndex;
    saveHideAndSeekProgress(true);
  }, [allCorrectFound, gameStarted, saveHideAndSeekProgress, wordIndex]);

  // move the guard periodically; interval depends on difficulty.
  useEffect(() => {
    if (!gameStarted) return;
    if (isGameOver) return;
    if (scatteredChars.length === 0) return;

    const moveGuard = () => {
      setGuardPosition((currentGuard) => {
        const blockedPositions = [...scatteredChars];
        if (currentGuard) {
          blockedPositions.push(currentGuard);
        }
        return generatePosition(blockedPositions);
      });
    };

    const interval = GUARD_MOVE_INTERVAL_MS[difficulty] ?? 10000;
    const guardTimer = setInterval(moveGuard, interval);
    return () => clearInterval(guardTimer);
  }, [difficulty, gameStarted, isGameOver, scatteredChars]);

  // In hard mode, hovering the guard for 1 second costs a life.
  useEffect(() => {
    const clearGuardHoverTimer = () => {
      if (guardHoverTimerRef.current) {
        clearTimeout(guardHoverTimerRef.current);
        guardHoverTimerRef.current = null;
      }
    };

    if (!gameStarted || isGameOver || !guardPosition) {
      clearGuardHoverTimer();
      guardPenaltyArmedRef.current = false;
      return undefined;
    }

    if (!boardRef.current || !guardRef.current) {
      clearGuardHoverTimer();
      return undefined;
    }

    const boardRect = boardRef.current.getBoundingClientRect();
    const guardRect = guardRef.current.getBoundingClientRect();

    const guardLeft = guardRect.left - boardRect.left;
    const guardRight = guardLeft + guardRect.width;
    const guardTop = guardRect.top - boardRect.top;
    const guardBottom = guardTop + guardRect.height;

    const cursorIsOnGuard =
      cursorPos.x >= guardLeft &&
      cursorPos.x <= guardRight &&
      cursorPos.y >= guardTop &&
      cursorPos.y <= guardBottom;

    if (cursorIsOnGuard) {
      if (!guardPenaltyArmedRef.current && !guardHoverTimerRef.current) {
        guardHoverTimerRef.current = setTimeout(() => {
          setLives((previousLives) => Math.max(0, previousLives - 1));
          setShowGuardCaughtIndicator(true);
          if (guardCaughtTimerRef.current) {
            clearTimeout(guardCaughtTimerRef.current);
          }
          guardCaughtTimerRef.current = setTimeout(() => {
            setShowGuardCaughtIndicator(false);
            guardCaughtTimerRef.current = null;
          }, 900);
          guardPenaltyArmedRef.current = true;
          guardHoverTimerRef.current = null;
        }, GUARD_HOVER_PENALTY_DELAY_MS);
      }
    } else {
      clearGuardHoverTimer();
      guardPenaltyArmedRef.current = false;
    }

    return clearGuardHoverTimer;
  }, [cursorPos, gameStarted, guardPosition, isGameOver, saveHideAndSeekProgress]);

  useEffect(() => {
    if (!gameStarted || !isTimeOut || allCorrectFound) {
      return;
    }

    if (loggedTimeoutWordRef.current === wordIndex) {
      return;
    }

    loggedTimeoutWordRef.current = wordIndex;
    saveHideAndSeekProgress(false);
  }, [allCorrectFound, gameStarted, isTimeOut, saveHideAndSeekProgress, wordIndex]);

  useEffect(() => {
    if (!gameStarted || !isOutOfLives || allCorrectFound) {
      return;
    }

    if (loggedLossWordRef.current === wordIndex) {
      return;
    }

    loggedLossWordRef.current = wordIndex;
    saveHideAndSeekProgress(false);
  }, [allCorrectFound, gameStarted, isOutOfLives, saveHideAndSeekProgress, wordIndex]);

  const handleReplay = () => {
    setLives(MAX_LIVES);
    setTimeLeft(roundTimeSeconds);
    setWrongId(null);
    setShowGuardCaughtIndicator(false);
    guardPenaltyArmedRef.current = false;
    loggedSuccessWordRef.current = null;
    loggedTimeoutWordRef.current = null;
    loggedLossWordRef.current = null;
    if (guardHoverTimerRef.current) {
      clearTimeout(guardHoverTimerRef.current);
      guardHoverTimerRef.current = null;
    }
    if (guardCaughtTimerRef.current) {
      clearTimeout(guardCaughtTimerRef.current);
      guardCaughtTimerRef.current = null;
    }
    buildRound(wordIndex);
  };

  const handleStartGame = () => {
    setWordIndex(0);
    setScatteredChars([]);
    setWrongId(null);
    setLives(MAX_LIVES);
    setTimeLeft(roundTimeSeconds);
    setShowGuardCaughtIndicator(false);
    guardPenaltyArmedRef.current = false;
    loggedSuccessWordRef.current = null;
    loggedTimeoutWordRef.current = null;
    loggedLossWordRef.current = null;
    if (guardHoverTimerRef.current) {
      clearTimeout(guardHoverTimerRef.current);
      guardHoverTimerRef.current = null;
    }
    if (guardCaughtTimerRef.current) {
      clearTimeout(guardCaughtTimerRef.current);
      guardCaughtTimerRef.current = null;
    }
  };

  const flashlightBackground = `
    radial-gradient(circle ${Math.round(FLASHLIGHT_RADIUS * 1.22)}px at ${cursorPos.x}px ${cursorPos.y}px,
      rgba(255, 239, 178, 0.14) 0%,
      rgba(255, 239, 178, 0.06) 18%,
      rgba(0, 0, 0, 0.08) 30%,
      rgba(0, 0, 0, 0.34) 46%,
      rgba(0, 0, 0, 0.72) 62%,
      rgba(0, 0, 0, 0.94) 74%,
      rgba(0, 0, 0, 1) 100%
    )
  `;

  let guardOverlay = null;
  if (guardPosition) {
    if (showGuardImage) {
      guardOverlay = (
        <img
          ref={guardRef}
          src={guardImage}
          alt="guard"
          className="hide-seek-hard-guard"
          style={{
            left: `${guardPosition.x}%`,
            top: `${guardPosition.y}%`,
          }}
          onError={() => setShowGuardImage(false)}
        />
      );
    } else {
      guardOverlay = (
        <div
          ref={guardRef}
          className="hide-seek-hard-guard-fallback"
          style={{
            left: `${guardPosition.x}%`,
            top: `${guardPosition.y}%`,
          }}
        >
          <span className="hide-seek-hard-guard-mask">□</span>
        </div>
      );
    }
  }

  // page opened without selecting a unit first.
  if (!unit) {
    return (
      <div className="hide-seek-page">
        <h2>Missing unit selection.</h2>
        <Link to={PRACTICE_PATH}>
          <button className="btn btn-primary">Back</button>
        </Link>
      </div>
    );
  }

  // Loading state for vocab data.
  if (loading || !currentWord) {
    return (
      <div className="hide-seek-page">
        <h2>Loading hide and seek audio...</h2>
      </div>
    );
  }

  // Error state only if no fallback words are available.
  if (error && gameWords.length === 0) {
    return (
      <div className="hide-seek-page">
        <h2>Error loading audio prompts: {error}</h2>
      </div>
    );
  }

  // pregame setup screen.
  if (!gameStarted) {
    return (
      <div className="hide-seek-page hide-seek-start-page">
        <audio ref={audioRefBgm} src={bgm} loop />
        <AudioControls volume={volume} setVolume={setVolume} isMuted={isMuted} setIsMuted={setIsMuted} />
        <SetUpBox
          unit={unit}
          category={category}
          onStartGame={() => {
            handleStartGame();
            setGameStarted(true);
          }}
          difficulty={difficulty}
          setDifficulty={setDifficulty}
        />
      </div>
    );
  }

  // Main game UI.
  return (
    <div className="hide-seek-page">
      <audio ref={audioRefBgm} src={bgm} loop />
      <AudioControls volume={volume} setVolume={setVolume} isMuted={isMuted} setIsMuted={setIsMuted} />
      <div className="hide-seek-header">
        <div className="hide-seek-header-left">
          <h1>Hide and Seek</h1>
          <p>{unit.name}</p>
          <button
            type="button"
            className="btn hide-seek-audio-button"
            onClick={playCurrentWordAudio}
            disabled={false}
          >
            &#9654; Hear Again
          </button>
        </div>
        <div className="hide-seek-header-badges">
          <div className="hide-seek-lives-badge">
            <span className="hide-seek-heart-icon">&#9829;</span>
            <span className="hide-seek-badge-num">{lives}</span>
          </div>
          <div className="hide-seek-timer-badge">
            <span className="hide-seek-badge-num">{timeLeft}</span>
          </div>
        </div>
      </div>

      <div
        ref={boardRef}
        className="hide-seek-board"
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchMove}
        onTouchMove={handleTouchMove}
      >
        {scatteredChars.map((character) => (
          <button
            key={character.id}
            type="button"
            className={`hide-seek-character ${
              character.isFound ? "found" : ""
            } ${wrongId === character.id ? "wrong" : ""}`}
            style={{
              left: `${character.x}%`,
              top: `${character.y}%`,
            }}
            onClick={(event) => handleCharacterClick(character, event)}
          >
            {character.char}
          </button>
        ))}
        {guardOverlay}
        <div
          className="hide-seek-flashlight"
          style={{
            background: flashlightBackground,
          }}
        />
        {showGuardCaughtIndicator && (
          <div className="hide-seek-guard-caught-indicator">
            Guard caught you!
          </div>
        )}
        {isGameOver && (
          <div className="hide-seek-gameover">
            <p className="hide-seek-gameover-reason">
              {isTimeOut ? "Time ran out!" : "Out of lives!"}
            </p>
            <p className="hide-seek-gameover-label">The answer was:</p>
            <div className="hide-seek-gameover-answer">
              {correctChars.map((char, index) => {
                if (char === " ") {
                  return <span key={`sp-${index}`} className="hide-seek-gameover-space" />;
                }
                const neededCount = correctChars
                  .slice(0, index + 1)
                  .filter((c) => c === char).length;
                const foundCount = scatteredChars.filter(
                  (c) => c.isCorrect && c.isFound && c.char === char
                ).length;
                const wasFound = foundCount >= neededCount;
                return (
                  <span
                    key={`${char}-${index}`}
                    className={`hide-seek-gameover-char ${wasFound ? "char-found" : "char-missed"}`}
                  >
                    {char}
                  </span>
                );
              })}
            </div>
            <button
              type="button"
              className="btn hide-seek-replay-button"
              onClick={handleReplay}
            >
              Replay
            </button>
          </div>
        )}
      </div>

      <div className="hide-seek-answer-slots">
        {correctChars.map((char, index) => {
          if (char === " ") {
            return <div key={`space-${index}`} className="slot-space" />;
          }

          const neededCount = correctChars
            .slice(0, index + 1)
            .filter((entry) => entry === char).length;
          const foundCount = scatteredChars.filter(
            (entry) => entry.isCorrect && entry.isFound && entry.char === char
          ).length;
          const isFilled = foundCount >= neededCount;

          return (
            <div key={`${char}-${index}`} className="slot-letter">
              {isFilled ? char : ""}
            </div>
          );
        })}
      </div>

      {allCorrectFound && (
        <p className="hide-seek-success">Correct! You found {currentWord.korean}</p>
      )}

      <Link
        to={GAME_OPTIONS_PATH}
        state={{ unit: unit, category: category }}
        className="game-options-back-link"
      >
        <button className="btn btn-back game-options-back-button">Back</button>
      </Link>
    </div>
  );
}

export default HideAndSeekPage;
